import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessstellenListeSummary } from '../../../../Models/messstelle.model';
import {
  IbnProtokollAuswahl,
  IbnProtokollDialogData,
  IbnProtokollMessstellenVariante,
  IbnProtokollUmfangAuswahl
} from './ibn-protokoll-auswahl.model';

// Die Typen liegen jetzt zentral im Auswahlmodell, damit sie alle drei
// Dialoge und der Aufrufer teilen. Der bisherige Importpfad bleibt gültig.
export type {
  IbnProtokollAuswahl,
  IbnProtokollDialogData,
  IbnProtokollMessstellenVariante,
  IbnProtokollUmfangAuswahl
} from './ibn-protokoll-auswahl.model';

/**
 * Auswahl des Umfangs vor dem PDF-Export des IBN-Protokolls.
 *
 * Der Dialog entscheidet nur, WAS exportiert wird:
 *
 *   Ganzes Projekt        → sofort exportierbar
 *   Eine Inbetriebnahme   → Inbetriebnahme wählen, dann exportierbar
 *   Messstellen detailliert → eine der drei Varianten wählen; die eigentliche
 *                             Messstellenauswahl passiert im Folgedialog
 *
 * Die Messstellen selbst werden hier bewusst nicht geladen: für ein Projekt
 * mit vielen Listen wäre ein Vorabladen aller Messstellen reine Verschwendung.
 */
@Component({
  selector: 'app-ibn-protokoll-dialog',
  templateUrl: './ibn-protokoll-dialog.component.html',
  styleUrls: ['./ibn-protokoll-dialog.component.css']
})
export class IbnProtokollDialogComponent implements OnInit {
  umfang: IbnProtokollUmfangAuswahl = 'projekt';

  /** Nur bei `umfang === 'messstelle'` von Bedeutung. */
  variante: IbnProtokollMessstellenVariante | null = null;

  inbetriebnahmen: MessstellenListeSummary[] = [];
  inbetriebnahmeId: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<IbnProtokollDialogComponent, IbnProtokollAuswahl | null>,
    @Inject(MAT_DIALOG_DATA) public data: IbnProtokollDialogData
  ) { }

  ngOnInit(): void {
    this.inbetriebnahmen = this.data.projekt?.inbetriebnahmen ?? [];

    // Bei genau einer Inbetriebnahme ist die Auswahl ohnehin eindeutig.
    if (this.inbetriebnahmen.length === 1) {
      this.inbetriebnahmeId = this.inbetriebnahmen[0].id;
    }
  }

  get hatInbetriebnahmen(): boolean {
    return this.inbetriebnahmen.length > 0;
  }

  waehleUmfang(umfang: IbnProtokollUmfangAuswahl): void {
    if (umfang !== 'projekt' && !this.hatInbetriebnahmen) {
      return;
    }

    this.umfang = umfang;

    if (umfang !== 'messstelle') {
      this.variante = null;
    }
  }

  waehleVariante(variante: IbnProtokollMessstellenVariante): void {
    if (!this.hatInbetriebnahmen) {
      return;
    }

    this.umfang = 'messstelle';
    this.variante = variante;
  }

  onInbetriebnahmeChange(id: number): void {
    this.inbetriebnahmeId = id;
  }

  get istGueltig(): boolean {
    if (this.umfang === 'projekt') {
      return true;
    }

    if (this.umfang === 'inbetriebnahme') {
      return this.inbetriebnahmeId != null;
    }

    return this.variante != null;
  }

  /**
   * Beim Projekt und bei einer Inbetriebnahme entsteht das PDF direkt.
   * Bei den Messstellen-Varianten folgt zuerst ein weiterer Dialog.
   */
  get istWeiter(): boolean {
    return this.umfang === 'messstelle';
  }

  abbrechen(): void {
    this.dialogRef.close(null);
  }

  exportieren(): void {
    if (!this.istGueltig) {
      return;
    }

    this.dialogRef.close({
      umfang: this.umfang,
      projektId: this.data.projekt.id,
      inbetriebnahmeId: this.umfang === 'inbetriebnahme'
        ? (this.inbetriebnahmeId ?? undefined)
        : undefined,
      variante: this.umfang === 'messstelle' ? (this.variante ?? undefined) : undefined,
      bezeichnung: this.bezeichnung()
    });
  }

  /** Name des gewählten Gegenstands – landet in der Rückmeldung des Aufrufers. */
  private bezeichnung(): string {
    if (this.umfang === 'inbetriebnahme') {
      return this.inbetriebnahmen.find(i => i.id === this.inbetriebnahmeId)?.name
        ?? 'Inbetriebnahme';
    }

    return this.data.projekt.name;
  }
}
